'use client';

import type { AutoFixLoopApiSource } from '@/lib/auto-fix-loop/analytics';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { autoFixLoopStatusQueryKey, postAutoFixLoopAction } from './auto-fix-loop-collection';
import {
  AUTO_FIX_RETRY_FAILED_MESSAGE,
  autoFixRetryNotice,
  autoFixRetryRefusal,
} from './retry-refusal';

export type AutoFixLoopRetryResult = { ok: true } | { ok: false; message: string };

async function postRetry(
  owner: string,
  repo: string,
  number: string,
  source: AutoFixLoopApiSource,
): Promise<AutoFixLoopRetryResult> {
  const result = await postAutoFixLoopAction(owner, repo, number, {
    action: 'retry',
    source,
  });
  if (!result.ok) {
    return { ok: false, message: result.error ?? AUTO_FIX_RETRY_FAILED_MESSAGE };
  }
  const refusal = autoFixRetryRefusal(result.data.dispatch, new Date());
  if (refusal) {
    return { ok: false, message: refusal };
  }
  // Every surface that retries through this hook says the wait here; each words its own refusals.
  const notice = autoFixRetryNotice(result.data.dispatch);
  if (notice) {
    toast(notice.title, { description: notice.description });
  }
  return { ok: true };
}

/**
 * Retries a failed auto-fix run from the supplied surface, in place. `retry` resolves to the API's refusal
 * instead of throwing, and stays pending until the status has refetched, so "retrying" gives way
 * to the new run rather than flashing the failure it replaces.
 */
export function useAutoFixLoopRetry(
  owner: string,
  repo: string,
  number: string,
  source: AutoFixLoopApiSource = 'pr_page',
) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => postRetry(owner, repo, number, source),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: autoFixLoopStatusQueryKey(owner, repo, number) }),
  });
  return { retry: () => mutateAsync(), isRetrying: isPending };
}
